import React, { FC } from 'react'
import styled from 'styled-components'
import { useHistory, useRouteMatch } from 'react-router-dom'

import Link from 'components/Link'
import Navbar from 'components/Navbar'
import { Box } from 'components/Box'
import { ShareListItemFragment, VersionFragment } from 'gqlTypes'
import HeaderNavigation from '../HeaderNavigation'
import UserNavbarSection from 'modules/user/components/UserNavbarSection'
import ShareDropdownItems, {
  ShareForShareActionsDropdown,
  VersionForShareActionsDropdown,
} from '../ShareActionsDropdown'
import HeaderWorkspaceBadge from '../HeaderWorkspaceBadge'
import { IsSignedIn } from 'containers/IsSignedIn'
import VersionLabel from 'components/VersionLabel'
import Dropdown from 'components/Dropdown'
import { Header as CwvHeader } from 'modules/shares/ComponentsWebView/components/Header'

import { useVersioning } from 'modules/versioning'
import { useForTablet } from 'components/Breakpoint'

import { ReactComponent as GearIcon } from 'icons/gear-24.svg'
import { ReactComponent as SidebarIcon } from 'icons/sidebar-right-20.svg'
import { ReactComponent as ArrowLeft } from 'icons/arrow.left-20.svg'

import { getDownloadProps, DownloadProps } from '../../utils'
import { routes } from 'constants/routes'
import { useFlag } from 'constants/flags'
import { breakpoint } from 'utils'
import { isCwvRoute } from 'modules/shares/ComponentsWebView/CwvRoutes'

const NavbarDivider = Navbar.Divider
const NavbarSection = Navbar.Section
const NavbarButton = Navbar.Button

/**
 * STYLES
 */
const NavbarFilter = styled(Navbar.Filter)`
  display: none;

  ${breakpoint('xs')`
    display: block;
  `}
`

const VersionLinkLabel = styled(VersionLabel)`
  margin-right: 16px;
`

const NavbarSectionBadge = styled(NavbarSection)`
  ${breakpoint('base', 'sm')`
    margin-right: -24px;
  `}
`

const NavbarSectionActions: React.FC = ({ children }) => (
  <NavbarSection align="end">{children}</NavbarSection>
)

/**
 * TYPES
 */
interface ShareActionsDropdownProps {
  share: ShareForShareActionsDropdown
  download: DownloadProps
  isViewingLatestVersion?: boolean
  version: VersionForShareActionsDropdown
}

export interface ShareForDocumentHeader
  extends ShareForShareActionsDropdown,
    Pick<
      ShareListItemFragment,
      | 'identifier'
      | 'userHasAcceptedInvitationToView'
      | 'userCanUpdateSettings'
      | 'workspace'
    > {}

export interface VersionForDocumentHeader {
  currentVersion: VersionFragment | null
  latestVersion: VersionFragment | null
}

export interface DocumentHeaderProps extends DocumentHeaderContainerProps {
  isViewingLatestVersion: boolean
  share: ShareForDocumentHeader
  download: DownloadProps
  version: VersionForDocumentHeader
}

/**
 * HELPER COMPONENTS
 */
const ShareActionsDropdown: FC<ShareActionsDropdownProps> = ({
  share,
  version,
  download,
  isViewingLatestVersion,
}) => {
  if (!share.userCanUpdateSettings && !share.userHasAcceptedInvitationToView) {
    return null
  }

  return (
    <Dropdown
      spacing="-4px"
      toggle={
        <NavbarButton
          size={24}
          data-testid="document-page-cog"
          icon={GearIcon}
          aria-label="Document Actions"
        />
      }>
      <ShareDropdownItems
        share={share}
        version={version}
        download={download}
        isViewingLatestVersion={isViewingLatestVersion}
      />
    </Dropdown>
  )
}

const HomeButton = () => {
  const history = useHistory()

  const closeDetailView = () => {
    history.push(routes.ENTRY.create({}))
  }

  return (
    <NavbarButton
      icon={ArrowLeft}
      size={20}
      onClick={closeDetailView}
      data-testid="detail-header-close"
    />
  )
}

/**
 * MAIN COMPONENT
 */
export const DocumentHeader: FC<DocumentHeaderProps> = ({
  isViewingLatestVersion,
  toggleSidebarRight,
  sidebarRightOpen,
  search,
  setSearchValue,
  setSearchCwv,
  isSidebarLeftOpen,
  setSidebarLeftOpen,
  share,
  version,
  download,
}) => {
  const { path } = useRouteMatch()
  const isComponentsWebViewOn = useFlag('components-web-view')
  const isTabletAndBigger = useForTablet()
  const isPublicShare =
    !share.workspace.userIsMember && !share.workspace.userIsOwner

  return (
    <>
      <Navbar isSidebarLeftOpen={isSidebarLeftOpen}>
        <NavbarSectionBadge>
          <IsSignedIn>
            {isPublicShare ? (
              <HomeButton />
            ) : (
              <HeaderWorkspaceBadge
                setSidebarLeftOpen={setSidebarLeftOpen}
                workspace={share.workspace!}
              />
            )}
          </IsSignedIn>
        </NavbarSectionBadge>
        <HeaderNavigation currentView="document" />
        <NavbarSectionActions>
          {!isViewingLatestVersion && isTabletAndBigger && (
            <>
              <VersionLinkLabel
                as={Link}
                to={routes.SHARE_VIEW.create({ shareID: share.identifier })}>
                see latest update
              </VersionLinkLabel>
              <NavbarDivider />
            </>
          )}
          {isComponentsWebViewOn && isCwvRoute(path) ? (
            <Box width={200} />
          ) : (
            <NavbarFilter
              value={search}
              width={200}
              onChange={setSearchValue}
              placeholder="Search Artboards"
            />
          )}
          <IsSignedIn>
            <ShareActionsDropdown
              share={share}
              version={version}
              download={download}
              isViewingLatestVersion={isViewingLatestVersion}
            />
          </IsSignedIn>
          <NavbarButton
            size={24}
            icon={SidebarIcon}
            active={sidebarRightOpen}
            onClick={toggleSidebarRight}
          />
          {isTabletAndBigger && (
            <>
              <NavbarDivider />
              <UserNavbarSection />
            </>
          )}
        </NavbarSectionActions>
      </Navbar>
      <CwvHeader
        setSearchCwv={setSearchCwv}
        shareID={share.identifier}
        isSidebarRightOpen={!!sidebarRightOpen}
      />
    </>
  )
}

export interface DocumentHeaderContainerProps {
  toggleSidebarRight: () => void
  sidebarRightOpen: boolean
  search: string
  setSearchValue: (value: string) => void
  setSearchCwv: (value: string) => void
  isSidebarLeftOpen?: boolean
  setSidebarLeftOpen?: React.Dispatch<React.SetStateAction<boolean>>
}

export const DocumentHeaderContainer: FC<DocumentHeaderContainerProps> = props => {
  const { share, isViewingLatestVersion, ...versioning } = useVersioning()
  const downloadProps = getDownloadProps(
    !versioning.loading ? versioning.currentVersion : null
  )
  const currentVersion = !versioning.loading ? versioning.currentVersion : null
  const latestVersion = !versioning.loading ? versioning.latestVersion : null

  const version = {
    currentVersion,
    latestVersion,
  }

  return (
    <DocumentHeader
      {...props}
      isViewingLatestVersion={isViewingLatestVersion}
      share={share}
      version={version}
      download={downloadProps}
    />
  )
}

export default DocumentHeaderContainer
